<script>
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  PieSeries,
  AccumulationLegend,
  AccumulationDataLabel,
  AccumulationTooltip,
} from '@syncfusion/ej2-vue-charts';

import { messagesMetricColors } from '../../../../../../../../../shared/helpers/CustomDashboardsHelper.js';

const smaller_1_Sizes = {
  containerWidth: '245px',
  containerHeight: '115px',
  containerPaddingTop: '40px',
  centerLabelTextSize: '9px',
  dataLabelTextSize: '8px',
  startAngle: '110',
};

const smaller_2_Sizes = {
  containerWidth: '300px',
  containerHeight: '140px',
  containerPaddingTop: '40px',
  centerLabelTextSize: '10px',
  dataLabelTextSize: '10px',
  startAngle: '150',
};
const mediumSizes = {
  containerWidth: '290px',
  containerHeight: '190px',
  containerPaddingTop: '0px',
  centerLabelTextSize: '11px',
  dataLabelTextSize: '11px',
  startAngle: '30',
};
const biggerSizes = {
  containerWidth: '500px',
  containerHeight: '250px',
  containerPaddingTop: '0px',
  centerLabelTextSize: '15px',
  dataLabelTextSize: '15px',
  startAngle: '45',
};

export default {
  components: {
    AccumulationChartComponent,
    AccumulationSeriesCollectionDirective,
    AccumulationSeriesDirective,
  },
  provide: {
    accumulationchart: [
      AccumulationLegend,
      PieSeries,
      AccumulationTooltip,
      AccumulationDataLabel,
    ],
  },
  props: {
    // Current cell size
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      theme: 'Material',

      chartArea: {
        border: {
          width: 0,
        },
      },
      radius: '70%',
      border: { width: 1 },
      legendSettings: {
        textWrap: 'Wrap',
        // maximumLabelWidth: 60,
        // position: '', // O default é pra baixo, possível tambem: 'Top'/'Left'/'Right'
        visible: false,
      },
      tooltip: {
        enable: true,
        duration: 200,
        // Header -> '<b>${point.x}<b>' | Valor -> '<b>${point.y}<b>'
        // eslint-disable-next-line no-template-curly-in-string
        format: '${point.x} : <b>${point.y}</b>',
      },
    };
  },
  computed: {
    seriesData() {
      return [
        // MENSAGENS ENVIADAS
        {
          Type: this.outgoingMessagesLabel,
          Messages: this.outgoingCount,
          DataLabelMappingName: `${this.outgoingMessagesLabel}: ${this.outgoingPercentage}%`,
        },
        // MENSAGENS RECEBIDAS
        {
          Type: this.incomingMessagesLabel,
          Messages: this.incomingCount,
          DataLabelMappingName: `${this.incomingMessagesLabel}: ${this.incomingPercentage}%`,
        },
      ];
    },
    centerLabel() {
      const textWhenNotHovered = 'Total <br>' + this.totalMessagesSum;

      // Header -> '<b>${point.x}<b>' | Valor -> '<b>${point.y}<b>'
      // eslint-disable-next-line no-template-curly-in-string
      const textWhenHovered = '${point.x} <br> ${point.y}';

      return {
        text: textWhenNotHovered,
        hoverTextFormat: textWhenHovered,
        textStyle: {
          fontWeight: '600',
          size: this.responsiveSizes.centerLabelTextSize,
        },
      };
    },
    dataLabel() {
      return {
        visible: true,
        name: 'DataLabelMappingName',
        position: 'Outside',
        font: {
          fontWeight: '600',
          size: this.responsiveSizes.dataLabelTextSize,
        },
        connectorStyle: {
          length: '0.5px',
          type: 'Line', // Posível colocar 'Curve'
        },
      };
    },

    incomingCount() {
      return this.data.incoming;
    },
    outgoingCount() {
      return this.data.outgoing;
    },
    totalMessagesSum() {
      const { outgoing, incoming } = this.data;
      return outgoing + incoming;
    },
    incomingPercentage() {
      if (!this.totalMessagesSum) return 0;

      return Math.round((this.incomingCount / this.totalMessagesSum) * 100);
    },
    outgoingPercentage() {
      if (!this.totalMessagesSum) return 0;

      const percentage = Math.round(
        (this.outgoingCount / this.totalMessagesSum) * 100
      );
      // Ensuring percentages sum will not exceed 100%
      const percentageSum = this.incomingPercentage + percentage;
      if (percentageSum === 100) return percentage;

      // Adjusting percentage to make the sum exactly 100%
      if (percentageSum < 100) return percentage + (100 - percentageSum);
      return percentage - (percentageSum - 100);
    },

    outgoingMessagesLabel() {
      return this.$t('CUSTOM_DASHBOARDS.MESSAGES.OUTGOING_MESSAGES');
    },
    incomingMessagesLabel() {
      return this.$t('CUSTOM_DASHBOARDS.MESSAGES.INCOMING_MESSAGES');
    },
    responsiveSizes() {
      if (this.sizeY < 2) {
        if (this.currentWidth < 350) return smaller_1_Sizes;
        return smaller_2_Sizes;
      }

      if (this.currentWidth < 500) return mediumSizes;
      return biggerSizes;
    },
  },
  methods: {
    pointRender(args) {
      if (args.point.x.indexOf(this.incomingMessagesLabel) > -1) {
        args.fill = messagesMetricColors.incoming;
      } else if (args.point.x.indexOf(this.outgoingMessagesLabel) > -1) {
        args.fill = messagesMetricColors.outgoing;
      } else {
        args.fill = '#FF9F40';
      }
    },
    generateUniqId() {
      return parseInt(Date.now() * Math.random().toFixed(5), 10);
    },
  },
};
</script>

<template>
  <div v-if="!totalMessagesSum" class="text-xs">
    {{ $t('CUSTOM_DASHBOARDS.MESSAGES.NO_MESSAGES') }}
  </div>
  <div
    v-else
    class="control-section"
    :style="{ 'padding-top': responsiveSizes.containerPaddingTop }"
  >
    <!-- :class="{ 'pt-10': currentWidth > 500 }" -->
    <div align="center">
      <AccumulationChartComponent
        :id="`chartcontainer-${generateUniqId()}`"
        :theme="theme"
        align="center"
        :chart-area="chartArea"
        :legend-settings="legendSettings"
        :center-label="centerLabel"
        :point-render="pointRender"
        :enable-border-on-mouse-move="false"
        :tooltip="tooltip"
        :width="responsiveSizes.containerWidth"
        :height="responsiveSizes.containerHeight"
        class="block-display"
      >
        <AccumulationSeriesCollectionDirective>
          <AccumulationSeriesDirective
            :data-source="seriesData"
            :border="border"
            :radius="radius"
            x-name="Type"
            y-name="Messages"
            :start-angle="responsiveSizes.startAngle"
            :data-label="dataLabel"
            inner-radius="70%"
            tooltip-mapping-name="text"
          />
        </AccumulationSeriesCollectionDirective>
      </AccumulationChartComponent>
    </div>
  </div>
</template>

<style>
.block-display {
  display: block;
}
</style>
